import { z } from 'zod';

import { FAILURE_CODES, NOTE_STATUSES } from '../notes/status';

/**
 * Zod boundary for the review screen's two routes, mirroring `backend/app/api/review.py` and
 * `backend/app/domain/review_document.py`. Every response is validated here rather than trusted
 * (`DEPENDENCIES.md` "6.1-6.6": "a shape change from the backend must fail loudly at the edge
 * instead of corrupting a review screen"). Money and quantity fields stay `string` end to end -
 * CLAUDE.md forbids binary floats for money, and a JSON number would already have lost exactness
 * by the time it reached this boundary.
 */

export const REVIEW_FIELD_NAMES = [
  'customer',
  'date',
  'folio',
  'product',
  'quantity',
  'unit_price',
  'line_amount',
  'note_total',
] as const;
export type ReviewFieldName = (typeof REVIEW_FIELD_NAMES)[number];

export const RESOLUTION_METHODS = [
  'exact',
  'alias',
  'fuzzy',
  'context',
  'model',
  'unresolved',
] as const;
export type ResolutionMethod = (typeof RESOLUTION_METHODS)[number];

export const REFERENCE_KINDS = ['customer', 'product'] as const;
export type ReferenceKind = (typeof REFERENCE_KINDS)[number];

export const ALERT_SEVERITIES = ['info', 'warning', 'error'] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];

export const AGREEMENTS = ['agrees', 'agrees_after_rounding', 'disagrees', 'uncheckable'] as const;
export type Agreement = (typeof AGREEMENTS)[number];

export const REVIEW_MODES = ['human', 'automatic'] as const;
export type ReviewMode = (typeof REVIEW_MODES)[number];

/** `backend/app/domain/enums.py`'s `DeliveryStatus` - the state of one frozen `excel_delivery`,
 * distinct from the note's own nine-state lifecycle (`notes/status.ts`). */
export const DELIVERY_STATUSES = ['pending', 'leased', 'delivered', 'attention'] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/** `backend/app/domain/enums.py`'s `ReceiptOutcome` - the only two durable conclusions an agent
 * may report about a macro attempt (`CLAUDE.md` rule 8: no blind retry either way). */
export const RECEIPT_OUTCOMES = ['verified', 'ambiguous'] as const;
export type ReceiptOutcome = (typeof RECEIPT_OUTCOMES)[number];

// --- the reviewed document itself (request and response share this shape) ----------------------

/**
 * A customer or sales concept as the note currently has it. `official_name`/`reference_value_id`
 * are read-only in this subphase - resolving a reference is 6.5's job
 * (`docs/implementation/06-mobile-web.md` 6.4 out-of-scope note) - so this screen only ever
 * echoes them back exactly as loaded.
 */
export const referenceFieldSchema = z.object({
  recognized_text: z.string().nullable(),
  official_name: z.string().nullable(),
  reference_value_id: z.string().nullable(),
});
export type ReferenceField = z.infer<typeof referenceFieldSchema>;

/** `backend/app/domain/review_document.py`'s `LINE_KEY_PATTERN`. */
export const LINE_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$/;

export const salesLineSchema = z.object({
  line_key: z.string(),
  product: referenceFieldSchema,
  quantity: z.string().nullable(),
  unit_price: z.string().nullable(),
  line_amount: z.string().nullable(),
});
export type SalesLine = z.infer<typeof salesLineSchema>;

export const annotationSchema = z.object({
  text: z.string(),
  line_key: z.string().nullable(),
  field_name: z.enum(REVIEW_FIELD_NAMES).nullable(),
});
export type Annotation = z.infer<typeof annotationSchema>;

export const reviewDocumentSchema = z.object({
  // Version 2 added `folio`. The server reads its stored version 1 documents as version 2 ones
  // with no folio and answers at the current version, so this boundary only ever sees 2
  // (`backend/app/domain/review_document.py`'s `READABLE_SCHEMA_VERSIONS`).
  schema_version: z.literal(2),
  customer: referenceFieldSchema,
  note_date: z.string().nullable(),
  /** The pad's own pre-printed serial number (`COTIZACION No.`, `NUMERO` on a remisión).
   * Internal reference only - it identifies the sheet of paper and is never sent to Excel. A
   * string, not a number: a leading zero is part of a folio. */
  folio: z.string().nullable(),
  lines: z.array(salesLineSchema),
  written_total: z.string().nullable(),
  annotations: z.array(annotationSchema),
  // Present on every response but never trusted - `ReviewDocument._drop_derived_keys` on the
  // server recomputes it on read, and this screen recomputes its own live value from
  // `review/arithmetic.ts` while the owner edits rather than reading this field mid-edit.
  calculated_total: z.string().nullable().optional(),
});
export type ReviewDocument = z.infer<typeof reviewDocumentSchema>;

/** What a save actually sends - `schema_version`/`calculated_total` handling matches the server:
 * the server recomputes `calculated_total` itself and ignores whatever a caller sends, so this
 * shape simply omits it. */
export const reviewDocumentRequestSchema = reviewDocumentSchema.omit({ calculated_total: true });
export type ReviewDocumentRequest = z.infer<typeof reviewDocumentRequestSchema>;

// --- evidence and validation -------------------------------------------------------------------

export const predictionSchema = z.object({
  id: z.string(),
  field_path: z.string(),
  field_name: z.enum(REVIEW_FIELD_NAMES),
  line_key: z.string().nullable(),
  recognized_text: z.string(),
  interpreted_value: z.string().nullable(),
  interpreted_decimal: z.string().nullable(),
  interpreted_reference_id: z.string().nullable(),
  resolution_method: z.enum(RESOLUTION_METHODS),
  candidates: z.array(z.string()),
  confidence: z.string().nullable(),
  confidence_band: z.string().nullable(),
  validation_flags: z.array(z.string()),
  bbox: z.record(z.string(), z.string()).nullable(),
  crop_asset_id: z.string().nullable(),
});
export type Prediction = z.infer<typeof predictionSchema>;

export const referenceIssueSchema = z.object({
  id: z.string(),
  kind: z.enum(REFERENCE_KINDS),
  field_path: z.string(),
  observed_text: z.string(),
  proposed_name: z.string().nullable(),
  created_at: z.string(),
});
export type ReferenceIssue = z.infer<typeof referenceIssueSchema>;

export const reviewAlertSchema = z.object({
  id: z.string(),
  type: z.string(),
  severity: z.enum(ALERT_SEVERITIES),
  message_key: z.string(),
  reference_issue_id: z.string().nullable(),
  reference_kind: z.enum(REFERENCE_KINDS).nullable(),
  reference_observed_text: z.string().nullable(),
  created_at: z.string(),
});
export type ReviewAlert = z.infer<typeof reviewAlertSchema>;

export const lineArithmeticSchema = z.object({
  position: z.number(),
  line_key: z.string(),
  quantity: z.string().nullable(),
  unit_price: z.string().nullable(),
  written_amount: z.string().nullable(),
  calculated_amount: z.string().nullable(),
  agreement: z.enum(AGREEMENTS),
  difference: z.string().nullable(),
});
export type LineArithmetic = z.infer<typeof lineArithmeticSchema>;

export const totalArithmeticSchema = z.object({
  written_total: z.string().nullable(),
  calculated_total: z.string().nullable(),
  rows_counted: z.number(),
  rows_missing_amount: z.number(),
  agreement: z.enum(AGREEMENTS),
  difference: z.string().nullable(),
});
export type TotalArithmetic = z.infer<typeof totalArithmeticSchema>;

export const arithmeticSchema = z.object({
  lines: z.array(lineArithmeticSchema),
  total: totalArithmeticSchema,
});
export type Arithmetic = z.infer<typeof arithmeticSchema>;

export const revisionSchema = z.object({
  id: z.string(),
  revision_no: z.number(),
  review_mode: z.enum(REVIEW_MODES),
  reviewed_by: z.string().nullable(),
  reviewed_at: z.string().nullable(),
  based_on_run_id: z.string().nullable(),
  created_at: z.string(),
});
export type Revision = z.infer<typeof revisionSchema>;

/** Note statuses that permit `PUT /notes/{id}/review` (`app/domain/note_state.py`'s
 * `SAVE_REVIEW` rule) that this subphase builds an editor for. The rule also allows
 * `exported`, but editing an already-delivered note raises a post-export alert whose required
 * warning text is 6.6's scope (`docs/implementation/06-mobile-web.md`), so that status is shown
 * read-only here rather than editable with an incomplete warning. */
export const EDITABLE_NOTE_STATUSES: ReadonlySet<string> = new Set([
  'awaiting_review',
  'blocked_reference',
]);

/** `backend/app/api/review.py`'s `DeliveryResponse`. No `payload` - it was already returned
 * verbatim by `POST /notes/{id}/ready` and this screen never needs to re-render it. */
export const deliveryResponseSchema = z.object({
  id: z.string(),
  status: z.enum(DELIVERY_STATUSES),
  created_at: z.string(),
  completed_at: z.string().nullable(),
  attempt_count: z.number(),
  last_error: z.string().nullable(),
});
export type DeliveryInfo = z.infer<typeof deliveryResponseSchema>;

/** `backend/app/api/review.py`'s `ReceiptResponse`. */
export const receiptResponseSchema = z.object({
  id: z.string(),
  outcome: z.enum(RECEIPT_OUTCOMES),
  inserted_row_count: z.number(),
  verified_at: z.string().nullable(),
  acknowledged_at: z.string(),
});
export type ReceiptInfo = z.infer<typeof receiptResponseSchema>;

export const noteImageSchema = z.object({
  id: z.string(),
  content_type: z.string(),
  byte_size: z.number(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  sha256: z.string(),
  perceptual_hash: z.string().nullable(),
});

export const noteDetailSchema = z.object({
  id: z.string(),
  status: z.enum(NOTE_STATUSES),
  created_at: z.string(),
  captured_at: z.string().nullable(),
  /** When the owner discarded this note. A discarded note stays readable - all of its evidence
   * is intact - but every write route refuses it until it is restored, so the screen renders
   * read-only on the strength of this one field. */
  discarded_at: z.string().nullable(),
  discarded_reason: z.string().nullable(),
  failure_code: z.enum(FAILURE_CODES).nullable(),
  image: noteImageSchema.nullable(),
  revision_token: z.string(),
  document: reviewDocumentSchema,
  arithmetic: arithmeticSchema,
  revision: revisionSchema.nullable(),
  revision_count: z.number(),
  recognition_run_id: z.string().nullable(),
  predictions: z.array(predictionSchema),
  reference_issues: z.array(referenceIssueSchema),
  alerts: z.array(reviewAlertSchema),
  open_reference_issues: z.number(),
  // --- delivered evidence (6.6) - always present in the response, `null` before approval ------
  ready_revision: revisionSchema.nullable(),
  ready_document: reviewDocumentSchema.nullable(),
  delivery: deliveryResponseSchema.nullable(),
  receipt: receiptResponseSchema.nullable(),
});
export type NoteDetail = z.infer<typeof noteDetailSchema>;

/** The body of `PUT /notes/{id}/review` - see `app/api/review.py`'s `ReviewSaveRequest`. */
export interface ReviewSaveRequestBody {
  readonly based_on_revision: string;
  readonly document: ReviewDocumentRequest;
}

/** `backend/app/api/ready.py`'s `ReadyResponse` - the body of `POST /notes/{id}/ready`, for
 * both a first approval (`201`) and an idempotent replay of one (`200`). The two are rendered
 * identically (`api/http.ts`'s `requestJson` does not even expose the status code to the
 * caller), matching `capture/api.ts`'s established convention for the same upload idempotency
 * shape - a repeated tap must never look different from the first successful one. */
export const readyResponseSchema = z.object({
  note_id: z.string(),
  status: z.enum(NOTE_STATUSES),
  ready_revision_id: z.string(),
  delivery_id: z.string(),
  payload_sha256: z.string(),
});
export type ReadyResult = z.infer<typeof readyResponseSchema>;
